import { Button } from "common/components/ui/Button";
import { DialogFooter, DialogHeader } from "common/components/ui/Dialog";
import { DrawerFooter, DrawerHeader } from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Campaign } from "common/types";

import { MANAGE_CAMPAIGNS_ACTIONS } from "../../constants";
import useCampaignMutations from "../../datahooks/useCampaignMutations";
import { startCampaignsBulkAction } from "../../utils";

type Actions = "start" | "pause" | "delete";

interface CampaignsActionsProps {
  action: keyof typeof MANAGE_CAMPAIGNS_ACTIONS;
  selectedCampaigns: Campaign[];
  onBack: () => void;
  onSuccess: () => void;
}

function getCampaignLabel(campaignCount: number) {
  return campaignCount === 1 ? "campaign" : "campaigns";
}

export default function CampaignsActions({
  action,
  selectedCampaigns,
  onBack,
  onSuccess,
}: CampaignsActionsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { deleteCampaign, isDeletingCampaign, updateCampaign } =
    useCampaignMutations();

  const eligibleCampaigns =
    MANAGE_CAMPAIGNS_ACTIONS[action].getEligibleItems(selectedCampaigns);
  const eligibleCount = eligibleCampaigns.length;

  function deleteCampaigns() {
    startCampaignsBulkAction(
      (campaign, abortSignal) =>
        deleteCampaign({ campaignId: campaign.id, abortSignal }),
      selectedCampaigns,
      (finishedItemsCount) =>
        finishedItemsCount === selectedCampaigns.length
          ? "Campaigns deleted"
          : `Deleted ${finishedItemsCount}/${selectedCampaigns.length} campaigns`,
      false,
    );

    onSuccess();
  }

  function startCampaigns() {
    startCampaignsBulkAction(
      (campaign, abortSignal) =>
        updateCampaign({
          campaignId: campaign.id,
          updates: { state: "ACTIVE" },
          abortSignal,
        }),
      eligibleCampaigns,
      (finishedItemsCount) =>
        finishedItemsCount === eligibleCampaigns.length
          ? "Campaigns started"
          : `Started ${finishedItemsCount}/${eligibleCampaigns.length} campaigns`,
      true,
    );

    onSuccess();
  }

  function pauseCampaigns() {
    startCampaignsBulkAction(
      (campaign, abortSignal) =>
        updateCampaign({
          campaignId: campaign.id,
          updates: { state: "PAUSED" },
          abortSignal,
        }),
      eligibleCampaigns,
      (finishedItemsCount) =>
        finishedItemsCount === eligibleCampaigns.length
          ? "Campaigns paused"
          : `Paused ${finishedItemsCount}/${eligibleCampaigns.length} campaigns`,
      true,
    );

    onSuccess();
  }

  const actionTypes: Record<
    Actions,
    {
      label: string;
      onAction: () => void;
    }
  > = {
    start: {
      label: "Start",
      onAction: startCampaigns,
    },
    pause: {
      label: "Pause",
      onAction: pauseCampaigns,
    },
    delete: {
      label: "Delete",
      onAction: deleteCampaigns,
    },
  };

  const { label, onAction } = actionTypes[action];

  const campaignsCount = selectedCampaigns.length;
  const titleCount = action === "delete" ? campaignsCount : eligibleCount;

  const titleCampaignsLabel = getCampaignLabel(titleCount);
  const buttonCount = action === "delete" ? campaignsCount : eligibleCount;
  const buttonCampaignsLabel = getCampaignLabel(buttonCount);
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const actionVerb = label.toLowerCase();

  let descriptionContent: string;
  const descriptionPrefix = `Are you sure you want to`;

  if (action === "delete") {
    descriptionContent = `${descriptionPrefix} ${actionVerb} ${campaignsCount} ${getCampaignLabel(campaignsCount)}?`;
  } else if (eligibleCount === 0) {
    descriptionContent =
      action === "start"
        ? "None of the selected campaigns can be started."
        : "None of the selected campaigns are active.";
  } else {
    descriptionContent = `${descriptionPrefix} ${actionVerb} ${eligibleCount} ${getCampaignLabel(eligibleCount)}?`;
  }

  const isPrimaryDisabled =
    (action === "start" || action === "pause") && eligibleCount === 0;

  return (
    <>
      <Header
        title={
          <>
            {label} {titleCampaignsLabel}?
          </>
        }
        subtitle={descriptionContent}
      />

      <Footer className="items-center justify-end">
        <Button
          variant="secondary-black"
          onClick={onBack}
          disabled={isDeletingCampaign}
        >
          Cancel
        </Button>

        <Button
          variant={action === "delete" ? "outline-danger" : "default-black"}
          onClick={onAction}
          isLoading={isDeletingCampaign}
          disabled={isPrimaryDisabled}
        >
          {label} {buttonCampaignsLabel}
        </Button>
      </Footer>
    </>
  );
}
